/**
 * Admin gating for the `/ops` dashboard.
 *
 * Two layers:
 *   1. Allowlist — the connected wallet pubkey must be in `VITE_ADMIN_PUBKEY`
 *      (comma-separated). Unset → only DEV builds are allowed (prod denies),
 *      so a misconfigured mainnet build fails closed, not open.
 *   2. Signature unlock — the admin signs a domain-separated challenge to prove
 *      key ownership (not just claim a pubkey). The unlock is cached in
 *      `sessionStorage` per pubkey so it's a one-click-per-session gate.
 *
 * v1 is read-only (public on-chain data), so the signature is defense-in-depth
 * + future-proofing for when write actions land here. Keep the domain separator
 * stable if a server ever needs to verify the same signature.
 */
import { useCallback, useMemo, useState } from "react";
import { useWallet } from "@solana/wallet-adapter-react";

/** Domain separator for the ops-admin sign-in challenge. Keep stable. */
export const OPS_ADMIN_DOMAIN = "solblox-ops-admin|v1";

/** Parsed allowlist from `VITE_ADMIN_PUBKEY` (comma-separated base58 pubkeys). */
export function adminAllowlist(): string[] {
  const raw = import.meta.env.VITE_ADMIN_PUBKEY as string | undefined;
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Is this pubkey allowed? With no allowlist configured, allow only in DEV (so
 * local work isn't blocked) and deny in prod (fail closed).
 */
export function isAdminPubkey(pubkey: string | null): boolean {
  if (pubkey === null) return false;
  const list = adminAllowlist();
  if (list.length === 0) return import.meta.env.DEV === true;
  return list.includes(pubkey);
}

function unlockKey(pubkey: string): string {
  return `ops.admin.unlocked:${pubkey}`;
}

export type AdminGateStatus =
  | "no-wallet" // wallet not connected
  | "denied" // connected, but not in the allowlist
  | "needs-signature" // allowed, but hasn't signed the challenge this session
  | "authorized";

export interface AdminGate {
  status: AdminGateStatus;
  /** The connected admin pubkey (base58), or null. */
  pubkey: string | null;
  /** Whether an allowlist is configured at all (ops UX hint). */
  allowlistConfigured: boolean;
  /** Sign the challenge to unlock this session. No-op unless `needs-signature`. */
  signIn: () => Promise<void>;
  /** Error from the last `signIn` attempt, if any. */
  signInError: string | null;
}

/** Drive the `/ops` admin gate from the connected wallet. */
export function useAdminGate(): AdminGate {
  const { publicKey, signMessage } = useWallet();
  const pubkey = publicKey?.toBase58() ?? null;
  const [signedTick, setSignedTick] = useState(0); // bump to re-read sessionStorage
  const [signInError, setSignInError] = useState<string | null>(null);

  const unlocked = useMemo(() => {
    void signedTick;
    if (pubkey === null) return false;
    if (typeof sessionStorage === "undefined") return false;
    return sessionStorage.getItem(unlockKey(pubkey)) === "1";
  }, [pubkey, signedTick]);

  const status: AdminGateStatus = useMemo(() => {
    if (pubkey === null) return "no-wallet";
    if (!isAdminPubkey(pubkey)) return "denied";
    return unlocked ? "authorized" : "needs-signature";
  }, [pubkey, unlocked]);

  const signIn = useCallback(async () => {
    setSignInError(null);
    if (pubkey === null || !isAdminPubkey(pubkey)) return;
    if (!signMessage) {
      setSignInError("Connected wallet does not support message signing.");
      return;
    }
    try {
      const issuedAt = Math.floor(Date.now() / 1000);
      const msg = `${OPS_ADMIN_DOMAIN}|${pubkey}|${issuedAt}`;
      await signMessage(new TextEncoder().encode(msg));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(unlockKey(pubkey), "1");
      }
      setSignedTick((t) => t + 1);
    } catch (err) {
      setSignInError(err instanceof Error ? err.message : String(err));
    }
  }, [pubkey, signMessage]);

  return {
    status,
    pubkey,
    allowlistConfigured: adminAllowlist().length > 0,
    signIn,
    signInError,
  };
}
